import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";

// ─── Types ────────────────────────────────────────────────────────────────────

type FloorId = "G" | "B1";
export type TransitionStyle = "liftShaft" | "zoomDissolve" | "radarScan";

interface FloorTransitionOverlayProps {
  visible: boolean;
  fromFloor: FloorId;
  toFloor: FloorId;
  /** @default "liftShaft" */
  style?: TransitionStyle;
  /** Called when the animation fully completes */
  onComplete?: () => void;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const FLOOR_LABELS: Record<FloorId, string> = {
  G: "Ground Floor",
  B1: "Basement 1",
};

const MONO: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
};

// ─── Shared sub-components ────────────────────────────────────────────────────

/** Animated pulsing dots */
const PulseDots: React.FC = () => (
  <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.2, 1, 0.2] }}
        transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.28 }}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: "#378ADD",
        }}
      />
    ))}
  </div>
);

/** Grid SVG floor plan silhouette */
const FloorPlanGrid: React.FC<{ size?: number }> = ({ size = 1 }) => (
  <svg
    width={240 * size}
    height={150 * size}
    style={{
      position: "absolute",
      inset: 0,
      opacity: 0.35,
      pointerEvents: "none",
    }}
  >
    {[80, 160].map((x) => (
      <line
        key={x}
        x1={x * size}
        y1={0}
        x2={x * size}
        y2={150 * size}
        stroke="#378ADD"
        strokeWidth={0.5}
      />
    ))}
    {[50, 100].map((y) => (
      <line
        key={y}
        x1={0}
        y1={y * size}
        x2={240 * size}
        y2={y * size}
        stroke="#378ADD"
        strokeWidth={0.5}
      />
    ))}
    {[
      [15, 10, 45, 28],
      [95, 10, 50, 28],
      [165, 10, 55, 28],
      [15, 58, 55, 22],
      [88, 58, 65, 22],
      [165, 58, 55, 22],
      [15, 110, 40, 25],
      [95, 110, 50, 25],
      [165, 110, 55, 25],
    ].map(([x, y, w, h], i) => (
      <rect
        key={i}
        x={x * size}
        y={y * size}
        width={w * size}
        height={h * size}
        fill="rgba(55,138,221,0.22)"
        rx={2}
      />
    ))}
  </svg>
);

// ─── Style A: Lift Shaft ──────────────────────────────────────────────────────

const LiftShaftAnimation: React.FC<{
  fromFloor: FloorId;
  toFloor: FloorId;
  goingDown: boolean;
  onComplete?: () => void;
}> = ({ fromFloor, toFloor, goingDown, onComplete }) => {
  const [phase, setPhase] = useState<
    "idle" | "doorsOpen" | "moving" | "arrived" | "doorsClose"
  >("idle");
  const [displayFloor, setDisplayFloor] = useState<FloorId>(fromFloor);

  useEffect(() => {
    const t: ReturnType<typeof setTimeout>[] = [];
    t.push(setTimeout(() => setPhase("doorsOpen"), 300));
    t.push(setTimeout(() => setPhase("moving"), 900));
    t.push(
      setTimeout(() => {
        setDisplayFloor(toFloor);
        setPhase("arrived");
      }, 2200),
    );
    t.push(setTimeout(() => setPhase("doorsClose"), 2700));
    t.push(
      setTimeout(() => {
        setPhase("idle");
        onComplete?.();
      }, 3500),
    );
    return () => t.forEach(clearTimeout);
  }, []);

  const doorsOpen =
    phase === "doorsOpen" || phase === "moving" || phase === "arrived";

  // Start at fromFloor position, move to toFloor, stay there — never return
  const hasArrived =
    phase === "arrived" ||
    phase === "doorsClose" ||
    (phase === "idle" && displayFloor === toFloor);
  const carY = hasArrived
    ? goingDown
      ? "44%"
      : "6%"
    : goingDown
      ? "6%"
      : "44%";

  const shaftLines = Array.from({ length: 14 }, (_, i) => (
    <div
      key={i}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: `${(i + 1) * 7.14}%`,
        height: 1,
        background: "rgba(55,138,221,0.07)",
      }}
    />
  ));

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "#080c18",
        overflow: "hidden",
      }}
    >
      {/* Shaft track */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 200,
          transform: "translateX(-50%)",
          borderLeft: "1px solid rgba(55,138,221,0.12)",
          borderRight: "1px solid rgba(55,138,221,0.12)",
        }}
      >
        {shaftLines}
      </div>

      {/* Floor markers */}
      {[
        { floor: goingDown ? fromFloor : toFloor, pos: "8%" },
        { floor: goingDown ? toFloor : fromFloor, pos: "76%" },
      ].map(({ floor, pos }) => (
        <div
          key={floor}
          style={{
            position: "absolute",
            left: "50%",
            top: pos,
            transform: "translateX(-50%)",
            textAlign: "center",
            opacity: displayFloor === floor ? 0.9 : 0.25,
            transition: "opacity 0.4s ease",
          }}
        >
          <div
            style={{
              ...MONO,
              fontSize: 9,
              letterSpacing: 2,
              color: "#00d2dc",
              textTransform: "uppercase",
            }}
          >
            {FLOOR_LABELS[floor]}
          </div>
          <div
            style={{
              width: 180,
              height: 1,
              background: "rgba(0,210,220,0.3)",
              margin: "4px auto 0",
            }}
          />
        </div>
      ))}

      {/* Elevator car */}
      <motion.div
        animate={{ top: carY }}
        transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          width: 180,
          height: 100,
          background: "rgba(0,210,220,0.18)",
          border: "1.5px solid rgba(0,210,220,0.7)",
          borderRadius: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          overflow: "hidden",
        }}
      >
        <FloorPlanGrid size={0.72} />

        {/* Doors */}
        <motion.div
          animate={{ width: doorsOpen ? "0%" : "50%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            background: "rgba(8,12,24,0.95)",
            borderRight: "1px solid rgba(0,210,220,0.4)",
          }}
        />
        <motion.div
          animate={{ width: doorsOpen ? "0%" : "50%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            background: "rgba(8,12,24,0.95)",
            borderLeft: "1px solid rgba(0,210,220,0.4)",
          }}
        />

        {/* Floor indicator */}
        <motion.div
          key={displayFloor}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            ...MONO,
            fontSize: 10,
            letterSpacing: 3,
            color: "#ffffff",
            textTransform: "uppercase",
            position: "relative",
          }}
        >
          {displayFloor}
        </motion.div>
        <motion.div
          key={`name-${displayFloor}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#ffffff",
            letterSpacing: 0.4,
            position: "relative",
          }}
        >
          {FLOOR_LABELS[displayFloor]}
        </motion.div>

        {/* Direction arrow */}
        <motion.div
          animate={{ y: goingDown ? [0, 4, 0] : [0, -4, 0] }}
          transition={{
            duration: 0.5,
            repeat: phase === "moving" ? Infinity : 0,
          }}
          style={{
            fontSize: 13,
            color: "rgba(0,210,220,0.9)",
            position: "relative",
          }}
        >
          {goingDown ? "▼" : "▲"}
        </motion.div>
      </motion.div>

      {/* Instruction */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        style={{
          position: "absolute",
          bottom: 32,
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,0.4)",
            letterSpacing: 0.5,
          }}
        >
          {goingDown ? "Take the elevator down to" : "Take the elevator up to"}
        </div>
        <div
          style={{
            fontSize: 17,
            fontWeight: 600,
            color: "#00d2dc",
            marginTop: 4,
          }}
        >
          {FLOOR_LABELS[toFloor]}
        </div>
        <PulseDots />
      </motion.div>
    </div>
  );
};

// ─── Style B: Zoom Dissolve ───────────────────────────────────────────────────

const ZoomDissolveAnimation: React.FC<{
  fromFloor: FloorId;
  toFloor: FloorId;
  goingDown: boolean;
  onComplete?: () => void;
}> = ({ fromFloor, toFloor, goingDown, onComplete }) => {
  const [activeFloor, setActiveFloor] = useState<FloorId>(fromFloor);
  const [cardPhase, setCardPhase] = useState<"zoomIn" | "zoomOut" | "idle">(
    "zoomIn",
  );
  const [statusText, setStatusText] = useState(
    `${goingDown ? "Elevator down" : "Elevator up"} to ${FLOOR_LABELS[toFloor]}`,
  );

  useEffect(() => {
    const t: ReturnType<typeof setTimeout>[] = [];
    t.push(setTimeout(() => setCardPhase("zoomOut"), 1400));
    t.push(
      setTimeout(() => {
        setActiveFloor(toFloor);
        setCardPhase("zoomIn");
      }, 1900),
    );
    t.push(
      setTimeout(() => setStatusText(`Now on ${FLOOR_LABELS[toFloor]}`), 2600),
    );
    t.push(
      setTimeout(() => {
        onComplete?.();
      }, 3500),
    );
    return () => t.forEach(clearTimeout);
  }, []);

  const cardVariants = {
    zoomIn: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
    },
    zoomOut: {
      scale: 1.35,
      opacity: 0,
      transition: { duration: 0.38, ease: "easeIn" },
    },
    idle: { scale: 0.6, opacity: 0, transition: { duration: 0 } },
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "#060a14",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {/* Ambient rings */}
      {[320, 220, 130].map((size, i) => (
        <motion.div
          key={size}
          animate={{ scale: [1, 1.25, 1], opacity: [0.12, 0, 0.12] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.6 }}
          style={{
            position: "absolute",
            width: size,
            height: size,
            borderRadius: "50%",
            border: "1px solid rgba(55,138,221,0.3)",
          }}
        />
      ))}

      {/* Floor card */}
      <motion.div
        animate={cardPhase}
        variants={cardVariants}
        initial={{ scale: 0.6, opacity: 0 }}
        style={{
          width: 250,
          height: 156,
          borderRadius: 10,
          background: "rgba(55,138,221,0.06)",
          border: "1px solid rgba(55,138,221,0.28)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <FloorPlanGrid />
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFloor}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.28 }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              position: "relative",
            }}
          >
            <div
              style={{
                ...MONO,
                fontSize: 9,
                letterSpacing: 3,
                color: "rgba(55,138,221,0.7)",
                textTransform: "uppercase",
              }}
            >
              {activeFloor}
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: 0.4,
              }}
            >
              {FLOOR_LABELS[activeFloor]}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Status text */}
      <AnimatePresence mode="wait">
        <motion.div
          key={statusText}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          style={{
            marginTop: 24,
            fontSize: 13,
            color: "rgba(255,255,255,0.38)",
            letterSpacing: 0.5,
          }}
        >
          {statusText}
        </motion.div>
      </AnimatePresence>
      <PulseDots />
    </div>
  );
};

// ─── Style C: Radar Scan ──────────────────────────────────────────────────────

const RadarScanAnimation: React.FC<{
  fromFloor: FloorId;
  toFloor: FloorId;
  goingDown: boolean;
  onComplete?: () => void;
}> = ({ fromFloor, toFloor, goingDown, onComplete }) => {
  const [displayFloor, setDisplayFloor] = useState<FloorId>(fromFloor);
  const [statusLines, setStatusLines] = useState<string[]>([
    "LOCATING ROUTE...",
  ]);
  const addStatus = (line: string) =>
    setStatusLines((prev) => [...prev.slice(-2), line]);

  useEffect(() => {
    const t: ReturnType<typeof setTimeout>[] = [];
    t.push(
      setTimeout(
        () => addStatus(`${goingDown ? "DESCENDING" : "ASCENDING"} — USE LIFT`),
        700,
      ),
    );
    t.push(setTimeout(() => addStatus("SWITCHING FLOOR MAP..."), 1500));
    t.push(setTimeout(() => setDisplayFloor(toFloor), 1900));
    t.push(setTimeout(() => addStatus("FLOOR MAP LOADED"), 2300));
    t.push(
      setTimeout(() => {
        onComplete?.();
      }, 3200),
    );
    return () => t.forEach(clearTimeout);
  }, []);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "#04080f",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {/* Pulsing rings */}
      {[300, 200, 110].map((d, i) => (
        <motion.div
          key={d}
          animate={{ scale: [1, 1.3, 1], opacity: [0.15, 0, 0.15] }}
          transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.55 }}
          style={{
            position: "absolute",
            width: d,
            height: d,
            borderRadius: "50%",
            border: "1px solid rgba(55,138,221,0.28)",
          }}
        />
      ))}

      {/* Center circle with sweep */}
      <div
        style={{
          width: 124,
          height: 124,
          borderRadius: "50%",
          border: "1.5px solid rgba(55,138,221,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Scan line */}
        <motion.div
          animate={{ top: ["0%", "100%"] }}
          transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            height: 2,
            background:
              "linear-gradient(to right, transparent, rgba(55,138,221,0.7), transparent)",
          }}
        />

        {/* Floor ID */}
        <AnimatePresence mode="wait">
          <motion.div
            key={displayFloor}
            initial={{ opacity: 0, y: goingDown ? 10 : -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: goingDown ? -10 : 10 }}
            transition={{ duration: 0.3 }}
            style={{ ...MONO, fontSize: 30, fontWeight: 500, color: "#378ADD" }}
          >
            {displayFloor}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Floor name */}
      <AnimatePresence mode="wait">
        <motion.div
          key={displayFloor}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            marginTop: 14,
            fontSize: 12,
            letterSpacing: 2,
            color: "rgba(255,255,255,0.45)",
            textTransform: "uppercase",
          }}
        >
          {FLOOR_LABELS[displayFloor]}
        </motion.div>
      </AnimatePresence>

      {/* Status terminal */}
      <div
        style={{
          marginTop: 20,
          width: 220,
          padding: "10px 14px",
          background: "rgba(55,138,221,0.04)",
          border: "1px solid rgba(55,138,221,0.12)",
          borderRadius: 6,
        }}
      >
        <AnimatePresence>
          {statusLines.map((line, i) => (
            <motion.div
              key={line}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: i === statusLines.length - 1 ? 1 : 0.3 }}
              style={{
                ...MONO,
                fontSize: 10,
                color: "#378ADD",
                letterSpacing: 1,
                lineHeight: 1.8,
              }}
            >
              {"> "}
              {line}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ─── Root Overlay ─────────────────────────────────────────────────────────────

const FloorTransitionOverlay: React.FC<FloorTransitionOverlayProps> = ({
  visible,
  fromFloor,
  toFloor,
  style = "liftShaft",
  onComplete,
}) => {
  const goingDown = toFloor === "B1";

  const renderAnimation = () => {
    const props = { fromFloor, toFloor, goingDown, onComplete };
    switch (style) {
      case "zoomDissolve":
        return <ZoomDissolveAnimation {...props} />;
      case "radarScan":
        return <RadarScanAnimation {...props} />;
      default:
        return <LiftShaftAnimation {...props} />;
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="floor-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
          }}
        >
          {renderAnimation()}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloorTransitionOverlay;

